import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import AlertControls from "src/components/ServerMonitoring/AlertControls";
import AppStatusCard from "src/components/ServerMonitoring/AppStatusCard";
import ServerActions from "src/components/ServerMonitoring/ServerActions";
import Section from "src/components/Shared/Containers/Section";
import Badge from "src/components/ServerMonitoring/Badge";
import StatTile from "src/components/ServerMonitoring/StatTile";
import { classNames } from "src/helpers/classNames";
import { formatDateTime, formatDuration, formatPercent, formatRelative, serverStatusStyles } from "src/helpers/serverMonitorHelper";

const formatSize = (mb) => (mb == null ? "—" : mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 ? 1 : 0)} GB` : `${Math.round(mb)} MB`);

const StatItem = ({ label, value, subValue = null, highlight = false }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <span className={classNames("text-lg font-semibold", highlight ? "text-red-600" : "text-gray-900")}>{value}</span>
    {subValue && <span className="text-xs text-gray-400">{subValue}</span>}
  </div>
);

const ServerStatusOverview = ({ status, loaded, checking, onCheckNow, onChanged = () => {} }) => {
  const insights = status?.node_insights;
  const style = serverStatusStyles[status?.status] || serverStatusStyles.unknown;
  const replicasShort = status?.replicas_ready != null && status?.replicas_desired != null && status.replicas_ready < status.replicas_desired;

  if (loaded && status && !status.configured) {
    return (
      <Section shadow>
        <div className="flex items-start gap-3 text-sm text-gray-600">
          <InformationCircleIcon className="h-6 w-6 flex-shrink-0 text-gray-400" />
          <div>
            <p className="font-semibold text-gray-900">Server monitoring is not configured</p>
            <p className="mt-1">
              Set <code className="rounded bg-gray-100 px-1">DIGITALOCEAN_TOKEN</code>, <code className="rounded bg-gray-100 px-1">DIGITALOCEAN_SAAS_APP_ID</code> and <code className="rounded bg-gray-100 px-1">DIGITALOCEAN_SAAS_COMPONENT_NAME</code> in the Super Portal API
              environment, then restart the API.
            </p>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <div className="space-y-6">
      <AppStatusCard
        app={status?.app}
        monitoredComponent={status?.component_name}
        monitoredStatus={status?.status}
        databases={status?.database?.databases || []}
        databaseStats={status?.database?.stats || null}
        loaded={loaded}
        checking={checking}
        configured={status?.configured}
        onCheckNow={onCheckNow}
      />
      <Section shadow>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Node Server</h2>
            {status?.component_name && <span className="text-sm text-gray-500">{status.component_name}</span>}
            {loaded && (
              <Badge
                label={style.label}
                className={style.badge}
                dot={style.dot}
              />
            )}
            {status?.digital_ocean_state && <span className="text-xs text-gray-400">DigitalOcean: {status.digital_ocean_state}</span>}
          </div>
          {status?.last_checked_at && (
            <span className="text-xs text-gray-400">
              Checked {formatRelative(status.last_checked_at)}
              {status?.interval_seconds ? ` · refreshed every ${status.interval_seconds}s` : ""}
            </span>
          )}
        </div>

        {status?.status === "crashed" && status?.active_incident && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span className="font-semibold">Node server is down</span> since {formatDateTime(status.active_incident.occurred_at, "MMM D, h:mm:ss A")}: {status.active_incident.reason}.
            {status.digital_ocean_state === "HEALTHY" && <span className="block text-xs text-red-700">DigitalOcean still reports HEALTHY - its health check can take several minutes to notice a Node process that died inside a running container.</span>}
          </div>
        )}

        {status?.monitoring_error && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">Monitoring problem</span> ({formatRelative(status.monitoring_error.at)}): {status.monitoring_error.message} The values below may be out of date. This is a Super Portal / DigitalOcean API issue, not a SaaS crash.
            </div>
          </div>
        )}

        {/* Live resources (like the Database section): bars, real units and 24h peaks */}
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="CPU"
            value={formatPercent(status?.cpu)}
            percent={status?.cpu}
            detail={[insights?.size?.cpus ? `${insights.size.cpus} vCPU${insights.size.cpu_type === "SHARED" ? " (shared)" : ""}` : null, insights?.cpu_peak_24h != null ? `Peak 24h: ${insights.cpu_peak_24h}%` : null].filter(Boolean).join(" · ") || null}
          />
          <StatTile
            label="Memory"
            value={formatPercent(status?.memory)}
            percent={status?.memory}
            detail={
              [insights?.size?.memory_mb && status?.memory != null ? `${formatSize((status.memory / 100) * insights.size.memory_mb)} of ${formatSize(insights.size.memory_mb)}` : null, insights?.memory_peak_24h != null ? `Peak 24h: ${insights.memory_peak_24h}%` : null]
                .filter(Boolean)
                .join(" · ") || null
            }
          />
          <StatTile
            label="Instances"
            value={status?.replicas_ready != null || status?.replicas_desired != null ? `${status?.replicas_ready ?? "—"} / ${status?.replicas_desired ?? "—"}` : "—"}
            percent={status?.replicas_desired ? Math.round(((status?.replicas_ready ?? 0) / status.replicas_desired) * 100) : null}
            tone={replicasShort ? "bad" : "default"}
            detail="ready / desired"
          />
          <StatTile
            label="Restarts (24h)"
            value={insights?.restarts_24h ?? "—"}
            tone={insights?.restarts_24h >= 3 ? "bad" : insights?.restarts_24h > 0 ? "warn" : "default"}
            detail={insights ? (insights.restarts_24h ? "Restarted by DigitalOcean after a crash" : "No crash restarts - deploys are not counted") : null}
          />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatItem
            label="Running Since"
            value={insights?.running_since && status?.status !== "crashed" ? formatDuration((Date.now() - new Date(insights.running_since).getTime()) / 1000) : "—"}
            subValue={insights?.running_since ? formatDateTime(insights.running_since, "MMM D, h:mm A") : null}
          />
          <StatItem
            label="Instance Size"
            value={<span className="text-sm">{insights?.size?.name || "—"}</span>}
            subValue={insights?.size?.usd_per_month ? `$${insights.size.usd_per_month}/month per instance` : null}
          />
          <StatItem
            label="Last Crash"
            value={status?.last_crash_at ? formatDateTime(status.last_crash_at, "MMM D, YYYY h:mm A") : "None recorded"}
            highlight={!!status?.active_incident}
            subValue={status?.active_incident ? "Not yet recovered" : null}
          />
          <StatItem
            label="Last Recovery"
            value={status?.last_recovery_at ? formatDateTime(status.last_recovery_at, "MMM D, YYYY h:mm A") : "—"}
          />
        </div>

        {/* Settings of this component worth changing in DigitalOcean (same style as the Database card notes) */}
        {status?.app?.config_warnings?.length > 0 && (
          <div className="mt-4 space-y-2">
            {status.app.config_warnings.map((warning) =>
              warning.level === "warning" ? (
                <div
                  key={warning.code}
                  className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                  <span>{warning.message}</span>
                </div>
              ) : (
                <div
                  key={warning.code}
                  className="flex items-start gap-2 text-sm text-gray-600">
                  <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span>{warning.message}</span>
                </div>
              ),
            )}
          </div>
        )}

        {status?.last_skipped_event?.reason === "deployment" && (
          <p className="mt-4 text-xs text-gray-400">
            Last restart signal at {formatDateTime(status.last_skipped_event.occurred_at)} was attributed to a deployment ({status.last_skipped_event.deployment?.phase}
            {status.last_skipped_event.deployment?.cause ? `: ${status.last_skipped_event.deployment.cause}` : ""}) and was not recorded as a crash.
          </p>
        )}

        <ServerActions
          status={status}
          onChanged={onChanged}
        />
      </Section>

      <AlertControls
        status={status}
        onChanged={onChanged}
      />
    </div>
  );
};

export default ServerStatusOverview;
